import * as R from 'ramda'
import React, { Children, useEffect, useState } from 'react'
import { useWindowSize } from '../../hooks/useWindowSize'
import { SetState } from '../../vite-env'
import { SliderContent } from '../Animations/Slider/SliderContent'
import { navigateToSlideIndex } from '../Animations/Slider/slider.helpers'
import { Step, StepProps } from './Step'
import { StepperFooter, StepperHeader } from './components'
import { twMerge } from 'tailwind-merge'
import { UseFormReturn } from 'react-hook-form'

type Props = (
  | {
      currentStep: number
      setCurrentStep: SetState<number>
    }
  | {
      defaultStep?: number
    }
) & {
  className?: string
  sliderClasses?: string
  children: (React.FunctionComponentElement<StepProps> | false)[] // Children Should have step props
  hideHeader?: boolean
  fullPage?: boolean
  hideFooter?: boolean
  /** Opt-in: pair with `<Stepper.Step fields={[...]}>` to have the
   *  footer auto-gate Next on rhf errors for those field paths and
   *  trigger validation on click. Most consumers should compute
   *  `disabled` per step instead — this is left in for convenience. */
  form?: UseFormReturn<any>
  addMarginAndSoftenEdges?: boolean
}

export const Stepper = (props: Props) => {
  const controlled = 'currentStep' in props && 'setCurrentStep' in props

  // const internalState = useState((props.defaultStep || 1) - 1)
  const [currentStepIndex, setCurrentStepIndex] = controlled
    ? [props.currentStep, props.setCurrentStep]
    : useState((props.defaultStep || 1) - 1)

  const [stepPropsList, setStepPropsList] = useState<StepProps[]>([])

  const [windowHeight] = useWindowSize()

  useEffect(() => {
    setStepPropsList(
      Children.toArray(props.children).map((child, i) => {
        // @ts-ignore
        const props = child.props as StepProps
        return {
          ...props,
          completed: i < currentStepIndex,
        }
      }),
    )
  }, [props.children, currentStepIndex])

  return (
    <section
      style={
        props.fullPage ? { height: windowHeight, maxHeight: windowHeight } : {}
      }
      className={twMerge(
        `w-full h-full flex flex-col justify-between items-stretch grow basis-0 overflow-clip`,
        props.addMarginAndSoftenEdges && '-m-8 p-8',
        props.className,
      )}>
      {!props.hideHeader && (
        <StepperHeader
          currentStep={currentStepIndex}
          stepPropsList={stepPropsList}
          onClickTitle={async (stepIndex) => {
            if (stepIndex > currentStepIndex) {
              // Header forward-nav mirrors the footer: the step being
              // left fires onNext, and a `false` return keeps the user
              // put so its side effects aren't silently skipped.
              const result =
                await stepPropsList[currentStepIndex]?.onNext?.()
              if (result === false) return
              setStepPropsList(
                R.set(R.lensPath([currentStepIndex, 'completed']), true),
              )
            }

            setCurrentStepIndex(
              navigateToSlideIndex(stepIndex, currentStepIndex, stepPropsList),
            )
          }}
        />
      )}

      <SliderContent
        addMarginAndSoftenEdges={props.addMarginAndSoftenEdges}
        selectedSlide={currentStepIndex}
        className={props.sliderClasses}>
        {props.children}
      </SliderContent>

      {!props.hideFooter && (
        <StepperFooter
          currentStepIndex={currentStepIndex}
          setCurrentStepIndex={setCurrentStepIndex}
          stepProps={stepPropsList}
          form={props.form}
        />
      )}
    </section>
  )
}

Stepper.Step = Step
